import { EyeIcon, LoaderCircleIcon } from 'lucide-react';
import type React from 'react';
import { useState } from 'react';
import { Button } from '../../../components/ui/button.tsx';
import { useToast } from '../../../components/ui/use-toast.tsx';
import { useLocale } from '../../../lib/hooks/use-locale.ts';
import { base64ToBlob } from '../../../lib/utils/base64-to-blob.ts';
import { api } from '../../../utils/api.ts';
import { useAnalytics } from '../../analytics/hooks/use-analytics.ts';
import type { OrderAttachment } from '../hooks/use-order-attachments.tsx';

type AttachmentPreviewButtonProps = {
    attachment: OrderAttachment;
};

export const AttachmentPreviewButton: React.FC<AttachmentPreviewButtonProps> = ({ attachment }) => {
    const { t } = useLocale('transaction-details');
    const { toast } = useToast();
    const sendEvent = useAnalytics();
    const apiUtils = api.useUtils();
    const [isLoading, setIsLoading] = useState(false);

    const handlePreview = async (e: React.MouseEvent) => {
        e.stopPropagation();

        if (isLoading) {
            return;
        }

        setIsLoading(true);
        try {
            const data = await apiUtils.platoUser.grid.getDetailQuoteOrder.getSalesDocument.fetch({
                documentId: attachment.documentId,
                documentType: attachment.documentType || '',
            });

            if (data.pdfBase64) {
                const pdfBlob = await base64ToBlob(data.pdfBase64, 'application/pdf');
                const url = URL.createObjectURL(pdfBlob);
                window.open(url, '_blank');

                sendEvent('transaction_document_viewed', {
                    document_id: attachment.documentId,
                    document_type: attachment.documentType || '',
                    document_name: attachment.documentName,
                });
            }
        } catch (err) {
            console.error('Error opening PDF:', err);
            toast({ icon: 'warning', title: t('attachments__error_preview') });
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <Button
            variant="subtle"
            size="icon"
            onClick={handlePreview}
            disabled={isLoading}
            aria-label={t('attachments__action_preview')}
        >
            {isLoading ? (
                <LoaderCircleIcon className="h-4 w-4 animate-spin" />
            ) : (
                <EyeIcon className="h-4 w-4" />
            )}
        </Button>
    );
};
